import { CATEGORY_NAMES } from "@/constants/dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { dispensaryHref } from "@/utils/dispensaryUrls";

import Carousel from "@/components/botanic/Carousel";
import MenuCard from "@/components/Dispensary/MenuCard";
import TrackImpression from "@/components/TrackImpression";

export type ProductRecTrayData = {
  menuItems: MenuItem[];
  name: string;
};

type Props = {
  productsForCategoryCarousels: ProductRecTrayData[];
  dispensarySlug: string;
  dispensaryRetailType: string;
};

const ProductRecTrays: React.FC<Props> = ({
  dispensarySlug,
  dispensaryRetailType,
  productsForCategoryCarousels = [],
}) => {
  const countryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const trackRecTrayEvent = (label: string) => {
    publishEvent({
      action: "Click",
      category: "Dispensary Menu Rec Tray",
      label,
    });
  };

  if (!productsForCategoryCarousels.length) {
    return null;
  }

  return (
    <>
      {productsForCategoryCarousels.map(({ name, menuItems = [] }) => {
        if (!menuItems.length) {
          return null;
        }

        const displayName = CATEGORY_NAMES[name] || name;

        const menuLink = dispensaryHref({
          additionalPath: `/menu?product_category[]=${name}`,
          countryCode,
          retailType: dispensaryRetailType,
          slug: dispensarySlug,
        });

        const trackCardClick = () => trackRecTrayEvent(displayName);
        const trackSeeAllClick = () =>
          trackRecTrayEvent(`${displayName} - show all`);

        return (
          <div key={name} data-testid="product-rec-tray">
            <Carousel
              seeMoreHref={menuLink}
              seeMoreOnClick={trackSeeAllClick}
              title={
                <h2
                  className="heading--m font-extrabold"
                  data-testid="section-header"
                >
                  {displayName}
                </h2>
              }
              data-testid={`${name}-carousel`}
            >
              {menuItems.map((item, i) => {
                if (item.promoted) {
                  const trackingCategory = "Dispensary Menu Rec Tray";
                  const trackingLabel = `${displayName} Lite-Amp Card`;
                  const trackingDimensions = {
                    brandId: item.brand?.id,
                    carouselSlot: i + 1,
                    dispensaryId: item.dispensaryId,
                    menuItemId: item.id,
                    productId: item.product?.id,
                  };

                  return (
                    <TrackImpression
                      category={trackingCategory}
                      action="impression"
                      className="h-full pb-sm"
                      customDimensions={trackingDimensions}
                      key={item.id}
                      label={trackingLabel}
                    >
                      <MenuCard
                        dispensaryId={item.dispensary.id}
                        menuItem={item}
                        onClick={() => {
                          trackCardClick();
                          publishEvent({
                            action: "Click",
                            category: trackingCategory,
                            label: trackingLabel,
                            ...trackingDimensions,
                          });
                        }}
                        onAddToBagClick={() => {
                          publishEvent({
                            action: "Click",
                            category: "Dispensary Menu Rec Tray",
                            label: `${trackingLabel} ATB`,
                            ...trackingDimensions,
                          });
                        }}
                      />
                    </TrackImpression>
                  );
                } else {
                  return (
                    <MenuCard
                      dispensaryId={item.dispensary.id}
                      menuItem={item}
                      onClick={trackCardClick}
                      key={item.id}
                    />
                  );
                }
              })}
            </Carousel>
          </div>
        );
      })}
    </>
  );
};

export default ProductRecTrays;
